import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { useAuth, apiErrorMessage } from "@/lib/auth";

/**
 * Shared admin login screen for /admin/leads and /admin/careers.
 * `title` and `subtitle` let the two dashboards brand their own login pages
 * without duplicating the form logic.
 */
export function AdminLogin({ title = "Admin", subtitle = "Sign in to continue." }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (e2) {
      setErr(apiErrorMessage(e2, "Invalid email or password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-screen bg-wafa-cream/40" data-testid="admin-login-page">
      <div className="mx-auto max-w-md px-4 py-24 lg:px-6">
        <div className="rounded-lg border border-wafa-deep/10 bg-white p-8">
          <h1 className="font-heading text-2xl font-semibold text-wafa-deep">{title}</h1>
          <p className="mt-2 text-sm text-wafa-ink/70">{subtitle}</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">Email</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">Password</label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary"
              />
            </div>
            {err ? (
              <div className="flex items-center gap-2 text-sm text-wafa-tomato" data-testid="admin-login-error">
                <AlertCircle className="h-4 w-4" /> {err}
              </div>
            ) : null}
            <button
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
